import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

// ==================== CUSTOM PRICE CRUD ====================

// 1. Create Custom Price (POST)
export const createCustomPrice = createAsyncThunk(
    'customPrice/createCustomPrice',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/custom-price`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 2. Get Custom Prices by Patient ID (GET)
export const getCustomPricesByPatientId = createAsyncThunk(
    'customPrice/getCustomPricesByPatientId',
    async ({ patientId, page = 1, limit = 5 }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/custom-price/patient/${patientId}`,
                {
                    params: { page, limit }
                }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 3. Get Custom Prices by Patient ID & Doctor ID (GET)
export const getCustomPricesByPatientAndDoctor = createAsyncThunk(
    'customPrice/getCustomPricesByPatientAndDoctor',
    async ({ patientId, doctorId, page = 1, limit = 5 }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/custom-price/patient/${patientId}/doctor/${doctorId}`,
                {
                    params: { page, limit }
                }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 4. Get Single Custom Price by Details (GET)
export const getSingleCustomPrice = createAsyncThunk(
    'customPrice/getSingleCustomPrice',
    async ({ patientId, doctorId, specialityId, appointmentTypeId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/custom-price/patient/${patientId}/doctor/${doctorId}/speciality/${specialityId}/appointment-type/${appointmentTypeId}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 5. Update Custom Price (PUT)
export const updateCustomPrice = createAsyncThunk(
    'customPrice/updateCustomPrice',
    async ({ uniqueId, payload }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(
                `${baseURL}/custom-price/${uniqueId}`,
                payload
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 6. Delete Custom Price (DELETE)
export const deleteCustomPrice = createAsyncThunk(
    'customPrice/deleteCustomPrice',
    async (uniqueId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/custom-price/${uniqueId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const customPriceSlice = createSlice({
    name: 'customPrice',
    initialState: {
        customPrices: [],
        selectedCustomPrice: null,
        pagination: {
            page: 1,
            limit: 10,
            totalPages: 0,
            totalItems: 0,
            previousPage: null,
            nextPage: null,
        },
        loading: false,
        error: null,
    },
    reducers: {
        clearCustomPriceError: (state) => {
            state.error = null;
        },
        clearSelectedCustomPrice: (state) => {
            state.selectedCustomPrice = null;
        },
        clearCustomPrices: (state) => {
            state.customPrices = [];
            state.pagination = {
                page: 1,
                limit: 10,
                totalPages: 0,
                totalItems: 0,
                previousPage: null,
                nextPage: null,
            };
        }
    },
    extraReducers: (builder) => {
        builder
            // Create Custom Price
            .addCase(createCustomPrice.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createCustomPrice.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.data) {
                    state.selectedCustomPrice = action.payload.data;
                }
            })
            .addCase(createCustomPrice.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Get Custom Prices by Patient ID
            .addCase(getCustomPricesByPatientId.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCustomPricesByPatientId.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.data) {
                    state.customPrices = action.payload.data;
                }
                if (action.payload?.page !== undefined) {
                    state.pagination = {
                        page: action.payload.page,
                        limit: action.payload.limit,
                        totalPages: action.payload.totalPages,
                        totalItems: action.payload.totalItems,
                        previousPage: action.payload.previousPage,
                        nextPage: action.payload.nextPage,
                    };
                }
            })
            .addCase(getCustomPricesByPatientId.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Get Custom Prices by Patient and Doctor
            .addCase(getCustomPricesByPatientAndDoctor.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCustomPricesByPatientAndDoctor.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.data) {
                    state.customPrices = action.payload.data;
                }
                if (action.payload?.page !== undefined) {
                    state.pagination = {
                        page: action.payload.page,
                        limit: action.payload.limit,
                        totalPages: action.payload.totalPages,
                        totalItems: action.payload.totalItems,
                        previousPage: action.payload.previousPage,
                        nextPage: action.payload.nextPage,
                    };
                }
            })
            .addCase(getCustomPricesByPatientAndDoctor.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Get Single Custom Price
            .addCase(getSingleCustomPrice.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSingleCustomPrice.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.data) {
                    state.selectedCustomPrice = action.payload.data;
                } else {
                    state.selectedCustomPrice = null; // Clear if no custom price data returned
                }
            })
            .addCase(getSingleCustomPrice.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.selectedCustomPrice = null; // Clear stale custom price on fetch failure
            })
            // Update Custom Price
            .addCase(updateCustomPrice.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateCustomPrice.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.data) {
                    state.selectedCustomPrice = action.payload.data;
                    // Update the item in the list if it exists
                    const index = state.customPrices.findIndex(
                        (item) => item.uniqueId === action.payload.data.uniqueId
                    );
                    if (index !== -1) {
                        state.customPrices[index] = action.payload.data;
                    }
                }
            })
            .addCase(updateCustomPrice.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Delete Custom Price
            .addCase(deleteCustomPrice.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteCustomPrice.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.data?.uniqueId) {
                    // Remove the item from the list
                    state.customPrices = state.customPrices.filter(
                        (item) => item.uniqueId !== action.payload.data.uniqueId
                    );
                }
            })
            .addCase(deleteCustomPrice.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { clearCustomPriceError, clearSelectedCustomPrice, clearCustomPrices } = customPriceSlice.actions;
export default customPriceSlice.reducer;

